import { inject, Injectable, NgZone } from "@angular/core";
import { map, Observable, Subject, switchMap } from "rxjs";
import { AIRequest } from "@modules/configuration-ia/models/ai-requests.model";
import { StorageService } from "@core/services/storages.service";
import { type ProvidersResponse, type AIMessageResponse, GetTemplatStatuseResponse, saveTemplateBody, saveTemplateResponse, GetTemplatStatuseFacebookResponse, generateTextEvent } from "@modules/configuration-ia/interfaces";
import { HttpClient } from "@angular/common/http";
import { environment as env } from '../../../../../../environments/environment';
import { TranslateService } from "@core/api/translate.service";

@Injectable({
  providedIn: 'root',
})
export class AITextGenerationService {
  private readonly aiRequest: AIRequest = inject(AIRequest);
  private readonly storageService = inject(StorageService);
  private readonly translateService = inject(TranslateService);
  private readonly API_URL = 'ai/agentes/textEnhanceOrGenerate';
  private readonly API_URL_IA = '/ai/agentes/assistant-ai';
  private streamAbortController: AbortController | null = null;
  private streamAbortOrigin: string | null = null;
  private idDistribuidor!: string;
  private idUser!: string;
  private idDistribuidorPlayground!: string;
  private readonly providersUrl = 'https://api.sicopweb.com/bi/qa/whatsapp';
  private readonly URL_TWILIO_STATUS = 'https://www.sicopweb.com/Api/Multicanal/Whatsapp/statusTemplate?data=';
  private readonly URL_META_STATUS = 'https://graph.facebook.com/v17.0/';

  private readonly URL_SAVE_TEMPLATES = 'https://qa3.sicopweb.com/Api/Multicanal/Templates/save';

  private streamTextSubject = new Subject<generateTextEvent>();
  streamText$ = this.streamTextSubject.asObservable();
  constructor(
    private http: HttpClient,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    const userInfo = this.storageService.getSessionStorage('userInfo')
    this.idDistribuidor = userInfo.IdDistribuidor ?? '';
    this.idDistribuidorPlayground = userInfo.IdBaseDeDatosPlayground ?? '';
    this.idUser = userInfo.IdUsuario ?? '';
  }
  // En función del contenido del mensaje recibido, lo mejora o lo genera
  enhanceOrGenerate(message: string, idMarca: string, tipo: 'mejora' | 'instruccion' | 'prompting' = 'mejora'): Observable<AIMessageResponse> {
    const body = {
      idDistribuidorConfig: this.storageService.getSessionStorage('userInfo').IdGrupoCorporativo,
      idMarca: idMarca,
      mensaje: message,
      tipo: tipo,
    };
    return this.aiRequest.Post<any>(this.API_URL, body).pipe(
      map((response: any) => {
        // Compatibilidad: API vieja { mensaje }, API nueva { data: { mensaje } }
        if (response?.mensaje !== undefined) return response as AIMessageResponse;
        if (response?.data?.mensaje !== undefined) {
          return { mensaje: response.data.mensaje } as AIMessageResponse;
        }
        return { mensaje: '' } as AIMessageResponse;
      })
    );
  }
  saveTemplateIA(name_template:string, message: string, botProvider: string, multilinePhone: string, img: string, isOptIn: boolean = false): Observable<saveTemplateResponse> {
    if (!this.idUser) {
      const userInfo = this.storageService.getSessionStorage('userInfo');
      this.idUser = userInfo?.IdUsuario ?? '';
    }
    const body: saveTemplateBody= {
      idDistribuidor: botProvider,
      idEjecutivo: this.idUser,
      from: multilinePhone,
      name_template:name_template,
      body: message,
      imageHeader: img, //Url encabezado - solo Meta
      buttons: isOptIn ? 'Si,No' : '',
      category: "5",
      type: 'CO', //CO, RE, RR
      conversacion: 0,
      campana: 0,
      momento: 0,
      bot: 0,
      cotizacion: 0
    }
    return this.http.post<saveTemplateResponse>(`${this.URL_SAVE_TEMPLATES}`, body).pipe();
  }
  getStatusPromting(sid_template: string, providerID: string = this.idDistribuidorPlayground, selectedTelephone: string,channelID: string = '0'): Observable<GetTemplatStatuseResponse[] | GetTemplatStatuseFacebookResponse> {
    return this.http.get<ProvidersResponse>(`${this.providersUrl}/config?iddistribuidor=${providerID}&canal=${channelID}`).pipe(
      switchMap(credentials => {
        const telefono = selectedTelephone;
        const proveedor = credentials.proveedor;
        const payload = {
          "from": telefono.trim(),
          "sid_template": sid_template.trim()
        };
        const jsonPayload = JSON.stringify(payload);
        const encodedPayload = encodeURIComponent(jsonPayload);
        if (proveedor === 0) {
          return this.http.get<GetTemplatStatuseResponse[]>(this.URL_TWILIO_STATUS + encodedPayload, {});
        } else if (proveedor === 1) {
          return this.http.get<GetTemplatStatuseFacebookResponse>(this.URL_META_STATUS + `${credentials.account_sid}/message_templates?access_token=${credentials.auth_token}`, {});
        } else {
          throw new Error('Proveedor desconocido');
        }
      })
    );
  }
  getStatusPromtingMultimarca(sid_template: string, selectedTelephone: string, idsubmarca: string): Observable<GetTemplatStatuseResponse[] | GetTemplatStatuseFacebookResponse> {
    const grupoCorporativo = this.storageService.getSessionStorage('userInfo').IdGrupoCorporativo
    return this.http.get<ProvidersResponse[]>(`${this.providersUrl}/config?idgrupocorporativoia=${grupoCorporativo}`).pipe(
      switchMap(credentials => {
        if(!idsubmarca || idsubmarca === '0') throw new Error('Submarca no seleccionada');
        if(!selectedTelephone) throw new Error('Telefono no seleccionado');
        const provider = credentials.find(provider => provider.idmarcasegmento === idsubmarca && provider.telefonodistribuidor === selectedTelephone);
        if(!provider) throw new Error(`Proveedor desconocido para la submarca ${idsubmarca} y el telefono ${selectedTelephone}`);
        const telefono = selectedTelephone;
        const proveedor = provider!.proveedor;
        const payload = {
          "from": telefono.trim(),
          "sid_template": sid_template.trim()
        };
        const jsonPayload = JSON.stringify(payload);
        const encodedPayload = encodeURIComponent(jsonPayload);
        if (proveedor === 0) {
          return this.http.get<GetTemplatStatuseResponse[]>(this.URL_TWILIO_STATUS + encodedPayload, {});
        } else if (proveedor === 1) {
          return this.http.get<GetTemplatStatuseFacebookResponse>(this.URL_META_STATUS + `${provider!.account_sid}/message_templates?access_token=${provider!.auth_token}`, {});
        } else {
          throw new Error('Proveedor desconocido');
        }
      })
    );
  }

  connectToAIStreamWithBody(text: string, idMarca: string, origin: string): void {
    if (this.streamAbortController) {
      try {
        this.streamAbortController.abort();
      } catch {
      }
      if (this.streamAbortOrigin) {
        this.streamTextSubject.next({ origin: this.streamAbortOrigin, message: '', done: true });
      }
    }

    this.streamAbortController = new AbortController();
    this.streamAbortOrigin = origin;
    const url = env.API_URL_IA_TEXT + this.API_URL_IA;
    const body = {
      texto: text,
      idMarca: idMarca,
      idDistribuidorConfig: this.storageService.getSessionStorage('userInfo').IdGrupoCorporativo
    };
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: this.streamAbortController.signal,
    }).then(response => {
      if (!response.body) throw new Error(this.translateService.instant('no_body_response', "La respuesta no contiene un cuerpo"));
      if (response.status !== 200) throw new Error(this.translateService.instant('error_ia_text_response','No se pudo generar el texto, revisar las credenciales del API de IA'));
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      const processStream = () => {
        reader.read().then(({ done, value }) => {
          if (done) {
            this.streamTextSubject.next({ origin: origin, message: '', done: true });
            return;
          }
          const chunk = decoder.decode(value, { stream: true }).replace(/^data:$/gm, '%salto').replace(/data:/g, '').replace(/[\r\n]+/g, '').replace(/%salto/g, '\n');
          this.streamTextSubject.next({origin: origin, message: (chunk === '\n' ? '' : chunk)});
          processStream();
        }).catch(error => {
          this.streamTextSubject.error(error);
        });
      };
      processStream();
    }).catch(error => {
      if (error?.name === 'AbortError') {
        this.streamTextSubject.next({ origin: origin, message: '', done: true });
        return;
      }
      this.streamTextSubject.error(error);
    });
  }
  resetStreamText(): void {
    this.streamTextSubject = new Subject<generateTextEvent>();
    this.streamText$ = this.streamTextSubject.asObservable();
  }
}
